import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { map, startWith } from 'rxjs';
import {
  MyClockNotificationsEnum,
  MyClockNotificationsEnumDescriptions,
  MyClockNotificationsEnumLabels,
} from 'src/app/enums/my-clock-notifications.enum';
import {
  OthersClockNotificationsEnum,
  OthersClockNotificationsEnumDescriptions,
  OthersClockNotificationsEnumLabels,
} from 'src/app/enums/others-clock-notifications.enum';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'app-user-detail-notifications-section',
  templateUrl: './user-detail-notifications-section.component.html',
  styleUrl: './user-detail-notifications-section.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatButton, MatIcon, MatFormField, MatSelect, MatOption],
})
export class UserDetailNotificationsSectionComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly permissions = inject(PermissionsService);

  readonly user_id = input.required<string>();
  readonly user = input.required<User>();
  readonly its_me = input.required<boolean>();
  readonly user_updated = output<User>();

  readonly form: UntypedFormGroup = this.fb.group({
    my_clock_notifications: [MyClockNotificationsEnum.All, Validators.required],
    others_clock_notifications: [OthersClockNotificationsEnum.Team, Validators.required],
  });
  private readonly form_status = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  private readonly my_clock_notifications_value = toSignal(
    this.form.controls['my_clock_notifications'].valueChanges.pipe(
      startWith(this.form.controls['my_clock_notifications'].value),
      map((value: MyClockNotificationsEnum): MyClockNotificationsEnum => value)
    ),
    { initialValue: MyClockNotificationsEnum.All }
  );
  private readonly others_clock_notifications_value = toSignal(
    this.form.controls['others_clock_notifications'].valueChanges.pipe(
      startWith(this.form.controls['others_clock_notifications'].value),
      map((value: OthersClockNotificationsEnum): OthersClockNotificationsEnum => value)
    ),
    { initialValue: OthersClockNotificationsEnum.Team }
  );

  readonly my_clock_notifications_options: MyClockNotificationsEnum[] = Object.values(MyClockNotificationsEnum);
  readonly others_clock_notifications_options: OthersClockNotificationsEnum[] = Object.values(OthersClockNotificationsEnum);
  readonly my_clock_notifications_enum_labels = MyClockNotificationsEnumLabels;
  readonly my_clock_notifications_enum_descriptions = MyClockNotificationsEnumDescriptions;
  readonly others_clock_notifications_enum_labels = OthersClockNotificationsEnumLabels;
  readonly others_clock_notifications_enum_descriptions = OthersClockNotificationsEnumDescriptions;
  readonly can_edit = computed((): boolean => this.its_me() || this.permissions.validateDepartment(['direction', 'rrhh']));
  readonly fields_disabled = computed((): boolean => !this.can_edit());
  readonly save_disabled = computed((): boolean => {
    this.form_status();

    return !this.form.valid || !this.can_edit();
  });
  readonly my_clock_notifications_description = computed(
    (): string => this.my_clock_notifications_enum_descriptions[this.my_clock_notifications_value()]
  );
  readonly others_clock_notifications_description = computed(
    (): string => this.others_clock_notifications_enum_descriptions[this.others_clock_notifications_value()]
  );

  ngOnInit(): void {
    this.form.patchValue({
      my_clock_notifications: this.user().my_clock_notifications || MyClockNotificationsEnum.All,
      others_clock_notifications: this.user().others_clock_notifications || OthersClockNotificationsEnum.Team,
    });
  }

  save_notifications(): void {
    const body: Record<string, unknown> = { ...this.form.value };

    this.api.updateUser(this.user_id(), body).subscribe((updated_user: User): void => {
      this.user_updated.emit(updated_user);
    });
  }
}
